<template>
  <div class="school-manage-container">

    <!-- ── PAGE HEADER ── -->
    <div class="page-header">
      <div class="breadcrumb">ຈັດການລະບົບ / <span>ຈັດການໂຮງຮຽນ</span></div>
      <div class="header-row">
        <div class="header-left">
          <h1><i class="fas fa-school"></i> ຈັດການຂໍ້ມູນໂຮງຮຽນ</h1>
          <p>ເພີ່ມ, ແກ້ໄຂ, ຄົ້ນຫາ ແລະ ຈັດການໂຮງຮຽນໃນລະບົບ</p>
        </div>
        <div class="header-actions">
          <button class="btn-export" @click="exportToExcel" :disabled="exporting">
            <i class="fas" :class="exporting ? 'fa-spinner fa-spin' : 'fa-file-export'"></i> ສົ່ງອອກ Excel
          </button>
          <button class="btn-import" @click="openImport">
            <i class="fas fa-file-excel"></i> ນຳເຂົ້າ Excel
          </button>
          <button class="btn-add" @click="openAdd">
            <i class="fas fa-plus-circle"></i> ເພີ່ມໂຮງຮຽນໃໝ່
          </button>
        </div>
      </div>
    </div>

    <!-- ── STATS STRIP ── -->
    <div class="stats-strip">
      <div class="stat-card">
        <div class="stat-icon blue"><i class="fas fa-school"></i></div>
        <div>
          <div class="stat-num">{{ totalRows }}</div>
          <div class="stat-lbl">ໂຮງຮຽນທັງໝົດ</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon green"><i class="fas fa-utensils"></i></div>
        <div>
          <div class="stat-num">{{ lunchCount }}</div>
          <div class="stat-lbl">ມີໂຄງການອາຫານທ່ຽງ</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon purple"><i class="fas fa-map-marker-alt"></i></div>
        <div>
          <div class="stat-num">{{ uniqueDistricts }}</div>
          <div class="stat-lbl">ເມືອງທີ່ຄົ້ນຫາ</div>
        </div>
      </div>
    </div>

    <!-- ── FILTER PANEL ── -->
    <div class="filter-panel">
      <div class="filter-row">
        <div class="search-wrap">
          <i class="fas fa-search search-icon"></i>
          <input
            v-model="search"
            type="text"
            placeholder="ຄົ້ນຫາຊື່ໂຮງຮຽນ ຫຼື ລະຫັດ EMIS..."
            @keyup.enter="doSearch"
          />
          <button v-if="search" class="clear-btn" @click="search=''; doSearch()">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <select v-model="filterProv" @change="onProvChange" class="filter-select">
          <option value="">-- ທຸກແຂວງ --</option>
          <option v-for="p in provinces" :key="p.value" :value="p.value">{{ p.text }}</option>
        </select>

        <select v-model="filterDist" @change="doSearch" class="filter-select" :disabled="!filterProv">
          <option value="">-- ທຸກເມືອງ --</option>
          <option v-for="d in districts" :key="d.value" :value="d.value">{{ d.text }}</option>
        </select>

        <select v-model="filterLevel" @change="doSearch" class="filter-select">
          <option value="">-- ທຸກຂັ້ນ --</option>
          <option v-for="l in levels" :key="l.id" :value="l.id">{{ l.name }}</option>
        </select>

        <button class="btn-search" @click="doSearch">
          <i class="fas fa-filter"></i> ຄົ້ນຫາ
        </button>
        <button class="btn-reset" @click="resetFilters">
          <i class="fas fa-undo"></i>
        </button>
      </div>
    </div>

    <!-- ── TABLE ── -->
    <div class="panel">
      <div class="panel-body">
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th style="width:46px">#</th>
                <th style="width:130px" class="th-sort" @click="setSort('school_id')">
                  ລະຫັດ EMIS <span class="sort-icon">{{ sortIcon('school_id') }}</span>
                </th>
                <th class="th-sort" @click="setSort('school_name_lao')">
                  ຊື່ໂຮງຮຽນ <span class="sort-icon">{{ sortIcon('school_name_lao') }}</span>
                </th>
                <th style="width:120px" class="th-sort" @click="setSort('prov_name')">
                  ແຂວງ <span class="sort-icon">{{ sortIcon('prov_name') }}</span>
                </th>
                <th style="width:120px" class="th-sort" @click="setSort('district_name_lao')">
                  ເມືອງ <span class="sort-icon">{{ sortIcon('district_name_lao') }}</span>
                </th>
                <th style="width:120px" class="th-sort" @click="setSort('school_level_lao')">
                  ຂັ້ນການສຶກສາ <span class="sort-icon">{{ sortIcon('school_level_lao') }}</span>
                </th>
                <th style="width:110px" class="th-sort" @click="setSort('schooltype')">
                  ປະເພດ <span class="sort-icon">{{ sortIcon('schooltype') }}</span>
                </th>
                <th style="width:90px" class="text-center th-sort" @click="setSort('school_lunch')">
                  ອາຫານທ່ຽງ <span class="sort-icon">{{ sortIcon('school_lunch') }}</span>
                </th>
                <th style="width:90px" class="text-center">ຈັດການ</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="9" class="td-center td-loading">
                  <i class="fas fa-spinner fa-spin"></i> ກຳລັງໂຫຼດ...
                </td>
              </tr>
              <tr v-else-if="schools.length === 0">
                <td colspan="9" class="td-empty">
                  <i class="fas fa-inbox fa-2x"></i>
                  <div>ບໍ່ພົບຂໍ້ມູນໂຮງຮຽນ</div>
                </td>
              </tr>
              <tr v-else v-for="(s, i) in schools" :key="s._id" :class="i % 2 === 0 ? 'row-even' : 'row-odd'">
                <td class="td-num">{{ (currentPage - 1) * pageSize + i + 1 }}</td>
                <td><span class="emis-badge">{{ s.school_id || '—' }}</span></td>
                <td class="td-name">
                  <div class="school-name">{{ s.school_name_lao || '—' }}</div>
                  <div class="school-sub" v-if="s.school_village">{{ s.school_village }}</div>
                </td>
                <td class="td-loc">{{ s.prov_name || '—' }}</td>
                <td class="td-loc">{{ s.district_name_lao || '—' }}</td>
                <td>
                  <span :class="['level-badge', levelClass(s.level_id)]">
                    {{ s.school_level_lao || '—' }}
                  </span>
                </td>
                <td>
                  <span :class="['type-badge', s.schooltype_code === 1 ? 'state' : 'private']">
                    {{ s.schooltype || '—' }}
                  </span>
                </td>
                <td class="text-center">
                  <span :class="['lunch-pill', s.school_lunch == 1 ? 'yes' : 'no']">
                    {{ s.school_lunch == 1 ? 'ມີ' : 'ບໍ່ມີ' }}
                  </span>
                </td>
                <td class="text-center">
                  <button class="action-btn edit" @click="openEdit(s)" title="ແກ້ໄຂ">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button class="action-btn delete" @click="confirmDel(s)" title="ລົບ">
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── PAGINATION ── -->
      <div class="pagination-bar">
        <div class="page-info">
          ສະແດງ <b>{{ startItem }}–{{ endItem }}</b> ຈາກ <b>{{ totalRows }}</b> ລາຍການ
        </div>
        <div class="page-controls">
          <button class="page-btn" :disabled="currentPage === 1" @click="changePage(1)">
            <i class="fas fa-angle-double-left"></i>
          </button>
          <button class="page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
            <i class="fas fa-angle-left"></i>
          </button>
          <button
            v-for="p in visiblePages" :key="p"
            class="page-btn"
            :class="{ active: p === currentPage, ellipsis: p === '...' }"
            :disabled="p === '...'"
            @click="p !== '...' && changePage(p)"
          >{{ p }}</button>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">
            <i class="fas fa-angle-right"></i>
          </button>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="changePage(totalPages)">
            <i class="fas fa-angle-double-right"></i>
          </button>
        </div>
        <select v-model.number="pageSize" @change="currentPage=1; fetchSchools()" class="page-size-select">
          <option :value="10">10 / ໜ້າ</option>
          <option :value="15">15 / ໜ້າ</option>
          <option :value="25">25 / ໜ້າ</option>
          <option :value="50">50 / ໜ້າ</option>
        </select>
      </div>
    </div>

    <!-- ─────────────────────────────────────────────────────────────
         MODAL: Add / Edit
    ──────────────────────────────────────────────────────────────── -->
    <transition name="modal-fade">
      <div v-if="modal.show" class="modal-overlay" @mousedown.self="closeModal">
        <div class="modal-card">
          <!-- Header -->
          <div class="modal-header">
            <div class="modal-title">
              <div class="modal-icon-wrap">
                <i :class="modal.isEdit ? 'fas fa-edit' : 'fas fa-plus-circle'"></i>
              </div>
              <span>{{ modal.isEdit ? 'ແກ້ໄຂຂໍ້ມູນໂຮງຮຽນ' : 'ເພີ່ມໂຮງຮຽນໃໝ່' }}</span>
            </div>
            <button class="modal-close" @click="closeModal"><i class="fas fa-times"></i></button>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <!-- Row 1: EMIS + ຊື່ -->
            <div class="form-row">
              <div class="form-group">
                <label>ລະຫັດ EMIS <span class="req">*</span></label>
                <input v-model="form.school_id" placeholder="ຕົວຢ່າງ: LA_SAV_SEP_001" :disabled="modal.isEdit" />
              </div>
              <div class="form-group flex2">
                <label>ຊື່ໂຮງຮຽນ (ພາສາລາວ) <span class="req">*</span></label>
                <input v-model="form.school_name_lao" placeholder="ໂຮງຮຽນປະຖົມ ..." />
              </div>
            </div>

            <!-- Row 2: Province + District -->
            <div class="form-row">
              <div class="form-group">
                <label>ແຂວງ <span class="req">*</span></label>
                <select v-model="form.prov_id" @change="onFormProvChange">
                  <option value="">-- ເລືອກແຂວງ --</option>
                  <option v-for="p in provinces" :key="p.value" :value="p.value">{{ p.text }}</option>
                </select>
              </div>
              <div class="form-group">
                <label>ເມືອງ <span class="req">*</span></label>
                <select v-model="form.district_id" @change="onFormDistChange" :disabled="!form.prov_id">
                  <option value="">-- ເລືອກເມືອງ --</option>
                  <option v-for="d in formDistricts" :key="d.value" :value="d.value">{{ d.text }}</option>
                </select>
              </div>
            </div>

            <!-- Row 3: ຕາແສງ + ບ້ານ -->
            <div class="form-row">
              <div class="form-group">
                <label>ຊື່ຕາແສງ</label>
                <input v-model="form.commune_name" placeholder="ຊື່ຕາແສງ" />
              </div>
              <div class="form-group">
                <label>ບ້ານທີ່ໂຮງຮຽນຕັ້ງຢູ່</label>
                <input v-model="form.school_village" placeholder="ຊື່ບ້ານ" />
              </div>
            </div>

            <!-- Row 4: ຂັ້ນ + ປະເພດ -->
            <div class="form-row">
              <div class="form-group">
                <label>ຂັ້ນການສຶກສາ <span class="req">*</span></label>
                <select v-model="form.level_id" @change="onLevelChange">
                  <option :value="null">-- ເລືອກຂັ້ນ --</option>
                  <option v-for="l in levels" :key="l.id" :value="l.id">{{ l.name }}</option>
                </select>
              </div>
              <div class="form-group">
                <label>ປະເພດໂຮງຮຽນ <span class="req">*</span></label>
                <select v-model="form.schooltype_code" @change="onTypeChange">
                  <option :value="null">-- ເລືອກປະເພດ --</option>
                  <option v-for="t in types" :key="t.id" :value="t.id">{{ t.name }}</option>
                </select>
              </div>
            </div>

            <!-- Row 5: ໂຄງການ -->
            <div class="form-row">
              <div class="form-group">
                <label>ໂຄງການອາຫານທ່ຽງ</label>
                <div class="radio-group">
                  <label class="radio-opt" :class="{ selected: form.school_lunch == 1 }">
                    <input type="radio" v-model.number="form.school_lunch" :value="1" />
                    <i class="fas fa-check-circle"></i> ມີໂຄງການ
                  </label>
                  <label class="radio-opt" :class="{ selected: form.school_lunch == 0 }">
                    <input type="radio" v-model.number="form.school_lunch" :value="0" />
                    <i class="fas fa-times-circle"></i> ບໍ່ມີໂຄງການ
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer">
            <button class="btn-cancel" @click="closeModal">
              <i class="fas fa-times"></i> ຍົກເລີກ
            </button>
            <button class="btn-save" :disabled="submitting" @click="handleSubmit">
              <i class="fas" :class="submitting ? 'fa-spinner fa-spin' : 'fa-save'"></i>
              {{ submitting ? 'ກຳລັງບັນທຶກ...' : (modal.isEdit ? 'ອັບເດດ' : 'ບັນທຶກ') }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ─────────────────────────────────────────────────────────────
         MODAL: Delete Confirm
    ──────────────────────────────────────────────────────────────── -->
    <transition name="modal-fade">
      <div v-if="delModal.show" class="modal-overlay" @mousedown.self="delModal.show = false">
        <div class="modal-card modal-sm">
          <div class="modal-header danger">
            <div class="modal-title">
              <div class="modal-icon-wrap danger"><i class="fas fa-trash-alt"></i></div>
              <span>ຢືນຢັນການລົບ</span>
            </div>
            <button class="modal-close" @click="delModal.show = false"><i class="fas fa-times"></i></button>
          </div>
          <div class="modal-body del-body">
            <p>ທ່ານຕ້ອງການລົບໂຮງຮຽນ</p>
            <div class="del-target">
              <i class="fas fa-school"></i>
              <strong>{{ delModal.item && delModal.item.school_name_lao }}</strong>
            </div>
            <p class="del-warn">ລະຫັດ EMIS: <code>{{ delModal.item && delModal.item.school_id }}</code></p>
            <p class="del-warn">ການກະທຳນີ້ <b>ບໍ່ສາມາດຍ້ອນຄືນໄດ້</b></p>
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click="delModal.show = false">ຍົກເລີກ</button>
            <button class="btn-danger" :disabled="delModal.loading" @click="doDelete">
              <i class="fas" :class="delModal.loading ? 'fa-spinner fa-spin' : 'fa-trash-alt'"></i>
              {{ delModal.loading ? 'ກຳລັງລົບ...' : 'ຢືນຢັນລົບ' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ─────────────────────────────────────────────────────────────
         MODAL: Import Excel
    ──────────────────────────────────────────────────────────────── -->
    <transition name="modal-fade">
      <div v-if="imp.show" class="modal-overlay" @mousedown.self="closeImport">
        <div class="modal-card modal-xl">

          <!-- Header -->
          <div class="modal-header import-header">
            <div class="modal-title">
              <div class="modal-icon-wrap"><i class="fas fa-file-excel"></i></div>
              <span>ນຳເຂົ້າຂໍ້ມູນໂຮງຮຽນຈາກ Excel</span>
            </div>
            <button class="modal-close" @click="closeImport"><i class="fas fa-times"></i></button>
          </div>

          <!-- STEP 1: File Select -->
          <div v-if="imp.step === 1" class="modal-body">
            <div class="import-info-box">
              <i class="fas fa-info-circle"></i>
              ໄຟລ໌ Excel ຕ້ອງມີຖັນ:
              <code>school_id</code>, <code>school_name_lao</code>, <code>district_id</code>
              (ທາງເລືອກ: prov_id, school_name_eng, commune_name, school_village, level_id, schooltype_code, school_lunch)
            </div>

            <div class="file-drop-zone" @click="$refs.xlsxInput.click()" @dragover.prevent @drop.prevent="onDrop">
              <i class="fas fa-cloud-upload-alt file-drop-icon"></i>
              <p>ລາກໄຟລ໌ມາວາງ ຫຼື <span class="file-drop-link">ກົດເພື່ອເລືອກ</span></p>
              <p class="file-drop-hint">.xlsx / .xls ເທົ່ານັ້ນ</p>
              <input ref="xlsxInput" type="file" accept=".xlsx,.xls" style="display:none" @change="onFileChange" />
            </div>

            <div v-if="imp.fileLoading" class="import-loading">
              <i class="fas fa-spinner fa-spin"></i> ກຳລັງອ່ານໄຟລ໌ ແລະ ກວດສອບລະຫັດໂຮງຮຽນ...
            </div>

            <div class="modal-footer" style="margin-top:0">
              <button class="btn-cancel" @click="closeImport">ຍົກເລີກ</button>
              <button class="btn-template" @click="downloadTemplate">
                <i class="fas fa-download"></i> ດາວໂຫຼດ Template
              </button>
            </div>
          </div>

          <!-- STEP 2: Preview -->
          <div v-if="imp.step === 2" class="modal-body">
            <!-- Summary -->
            <div class="import-summary">
              <span class="imp-badge total">ທັງໝົດ {{ imp.rows.length }}</span>
              <span class="imp-badge new"><i class="fas fa-plus-circle"></i> ໃໝ່: {{ imp.newCount }}</span>
              <span class="imp-badge dup"><i class="fas fa-exclamation-triangle"></i> ມີຢູ່ແລ້ວ: {{ imp.dupCount }}</span>
              <span v-if="imp.errCount > 0" class="imp-badge err"><i class="fas fa-times-circle"></i> ຂໍ້ມູນບໍ່ຄົບ: {{ imp.errCount }}</span>
            </div>

            <!-- Preview table -->
            <div class="import-table-wrap">
              <table class="import-table">
                <thead>
                  <tr>
                    <th style="width:90px">ສະຖານະ</th>
                    <th>ລະຫັດ EMIS</th>
                    <th>ຊື່ໂຮງຮຽນ (ລາວ)</th>
                    <th>ລະຫັດເມືອງ</th>
                    <th>ລະຫັດແຂວງ</th>
                    <th>ຂັ້ນ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(r, i) in imp.rows" :key="i" :class="'imp-row-' + r._st">
                    <td>
                      <span :class="'imp-status ' + r._st">
                        <i :class="statusIcon(r._st)"></i> {{ statusLabel(r._st) }}
                      </span>
                    </td>
                    <td><code>{{ r.school_id || '—' }}</code></td>
                    <td>{{ r.school_name_lao || '—' }}</td>
                    <td>{{ r.district_id || '—' }}</td>
                    <td>{{ r.prov_id || '—' }}</td>
                    <td>{{ levelName(r.level_id) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="modal-footer" style="margin-top:0">
              <button class="btn-cancel" @click="imp.step = 1">
                <i class="fas fa-arrow-left"></i> ກັບຄືນ
              </button>
              <span v-if="imp.newCount === 0" class="no-new-msg">ບໍ່ມີລາຍການໃໝ່</span>
              <button v-else class="btn-save" :disabled="imp.importing" @click="doImport">
                <i class="fas" :class="imp.importing ? 'fa-spinner fa-spin' : 'fa-cloud-upload-alt'"></i>
                {{ imp.importing ? 'ກຳລັງນຳເຂົ້າ...' : 'ນຳເຂົ້າ ' + imp.newCount + ' ລາຍການ' }}
              </button>
            </div>
          </div>

          <!-- STEP 3: Success -->
          <div v-if="imp.step === 3" class="modal-body import-done">
            <i class="fas fa-check-circle import-done-icon"></i>
            <h3>ນຳເຂົ້າສຳເລັດ!</h3>
            <p>ເພີ່ມ <b>{{ imp.importedCount }}</b> ໂຮງຮຽນເຂົ້າລະບົບສຳເລັດແລ້ວ.</p>
            <button class="btn-save" @click="closeImport">ປິດ</button>
          </div>

        </div>
      </div>
    </transition>

    <!-- ─────────────────────────────────────────────────────────────
         TOAST
    ──────────────────────────────────────────────────────────────── -->
    <transition name="toast-slide">
      <div v-if="toast.show" :class="['toast', toast.type]">
        <i :class="toast.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
        {{ toast.msg }}
      </div>
    </transition>

  </div>
</template>

<script>
import api from '@/utils/api';
import * as XLSX from 'xlsx';

const LEVELS = [
  { id: 1, name: 'ອະນຸບານ' },
  { id: 2, name: 'ປະຖົມ' },
  { id: 3, name: 'ມັດທະຍົມຕົ້ນ' },
  { id: 4, name: 'ມັດທະຍົມປາຍ' },
];
const TYPES = [
  { id: 1, name: 'ໂຮງຮຽນລັດ' },
  { id: 2, name: 'ໂຮງຮຽນເອກະຊົນ' },
];
const EMPTY_FORM = () => ({
  school_id: '',
  school_name_lao: '',
  prov_id: '',
  prov_name: '',
  district_id: '',
  district_name_lao: '',
  commune_name: '',
  school_village: '',
  level_id: null,
  school_level_lao: '',
  schooltype_code: null,
  schooltype: '',
  school_lunch: 1,
});

export default {
  name: 'SchoolManagePage',
  data() {
    return {
      schools: [],
      totalRows: 0,
      lunchCount: 0,
      exporting: false,
      currentPage: 1,
      pageSize: 15,
      loading: false,
      search: '',
      filterProv: '',
      filterDist: '',
      filterLevel: '',
      sortKey: 'school_name_lao',
      sortDir: 'asc',
      provinces: [],
      districts: [],
      formDistricts: [],
      levels: LEVELS,
      types: TYPES,
      form: EMPTY_FORM(),
      modal: { show: false, isEdit: false },
      submitting: false,
      delModal: { show: false, item: null, loading: false },
      toast: { show: false, type: 'success', msg: '' },
      imp: {
        show: false, step: 1, fileLoading: false,
        rows: [], existingIds: new Set(),
        importing: false, importedCount: 0,
        get newCount() { return this.rows.filter(r => r._st === 'new').length; },
        get dupCount() { return this.rows.filter(r => r._st === 'dup').length; },
        get errCount() { return this.rows.filter(r => r._st === 'err').length; },
      },
    };
  },
  computed: {
    totalPages() { return Math.ceil(this.totalRows / this.pageSize) || 1; },
    startItem() { return this.totalRows === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1; },
    endItem()   { return Math.min(this.currentPage * this.pageSize, this.totalRows); },
    uniqueDistricts() { return [...new Set(this.schools.map(s => s.district_id))].filter(Boolean).length; },
    visiblePages() {
      const total = this.totalPages;
      const cur   = this.currentPage;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const pages = [1];
      if (cur > 3)       pages.push('...');
      for (let p = Math.max(2, cur - 1); p <= Math.min(total - 1, cur + 1); p++) pages.push(p);
      if (cur < total - 2) pages.push('...');
      pages.push(total);
      return pages;
    },
  },
  mounted() {
    this.fetchProvinces();
    this.fetchSchools();
  },
  methods: {
    async fetchProvinces() {
      try {
        const res = await api.get('/getItems/province/prov_name/prov_id');
        this.provinces = Array.isArray(res.data) ? res.data : (res.data.rows || []);
      } catch (e) { console.error(e); }
    },
    async fetchDistricts(provId, target = 'districts') {
      if (!provId) { this[target] = []; return; }
      try {
        const res = await api.get(`/getItemsCondition/district/district_name_lao/district_id/prov_id/${provId}`);
        this[target] = Array.isArray(res.data) ? res.data : (res.data.rows || []);
      } catch (e) { console.error(e); }
    },
    async fetchSchools() {
      this.loading = true;
      try {
        const res = await api.get('/schools_manage', {
          params: {
            page:     this.currentPage,
            limit:    this.pageSize,
            search:   this.search,
            prov_id:   this.filterProv,
            dist_id:   this.filterDist,
            level_id:  this.filterLevel || undefined,
            sort_key: this.sortKey,
            sort_dir: this.sortDir,
          }
        });
        if (res.data.code === 1) {
          this.schools    = res.data.rows;
          this.totalRows  = res.data.totalRows;
          this.lunchCount = res.data.lunchCount || 0;
        } else {
          this.schools    = [];
          this.totalRows  = 0;
          this.lunchCount = 0;
        }
      } catch (e) {
        console.error(e);
        this.schools = [];
      } finally {
        this.loading = false;
      }
    },
    async exportToExcel() {
      this.exporting = true;
      try {
        const res = await api.get('/schools_manage', {
          params: {
            page:     1,
            limit:    100000,
            search:   this.search,
            prov_id:   this.filterProv,
            dist_id:   this.filterDist,
            level_id:  this.filterLevel || undefined,
            sort_key: this.sortKey,
            sort_dir: this.sortDir,
          }
        });
        if (res.data.code !== 1 || !res.data.rows.length) {
          alert('ບໍ່ມີຂໍ້ມູນທີ່ຈະສົ່ງອອກ.');
          return;
        }

        const header = ['ລຳດັບ', 'ລະຫັດ EMIS', 'ຊື່ໂຮງຮຽນ', 'ແຂວງ', 'ເມືອງ', 'ຂັ້ນການສຶກສາ', 'ປະເພດ', 'ອາຫານທ່ຽງ'];
        const data = res.data.rows.map((s, i) => [
          i + 1,
          s.school_id || '',
          s.school_name_lao || '',
          s.prov_name || '',
          s.district_name_lao || '',
          s.school_level_lao || '',
          s.schooltype || '',
          s.school_lunch == 1 ? 'ມີ' : 'ບໍ່ມີ',
        ]);

        const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'ຂໍ້ມູນໂຮງຮຽນ');

        const ts = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(wb, `ຂໍ້ມູນໂຮງຮຽນ_${ts}.xlsx`);
      } catch (error) {
        console.error('Excel Export Error:', error);
        alert('ເກີດຂໍ້ຜິດພາດໃນການສົ່ງອອກໄຟລ໌ Excel');
      } finally {
        this.exporting = false;
      }
    },
    doSearch()    { this.currentPage = 1; this.fetchSchools(); },
    changePage(p) { if (p >= 1 && p <= this.totalPages) { this.currentPage = p; this.fetchSchools(); } },
    resetFilters() {
      this.search = ''; this.filterProv = ''; this.filterDist = ''; this.filterLevel = ''; this.districts = [];
      this.currentPage = 1; this.fetchSchools();
    },
    async onProvChange() {
      this.filterDist = '';
      await this.fetchDistricts(this.filterProv, 'districts');
      this.doSearch();
    },
    async onFormProvChange() {
      this.form.prov_name     = (this.provinces.find(p => p.value === this.form.prov_id) || {}).text || '';
      this.form.district_id   = '';
      this.form.district_name_lao = '';
      await this.fetchDistricts(this.form.prov_id, 'formDistricts');
    },
    onFormDistChange() {
      const d = this.formDistricts.find(d => d.value === this.form.district_id);
      this.form.district_name_lao = d ? d.text : '';
    },
    onLevelChange() {
      const l = this.levels.find(l => l.id === this.form.level_id);
      this.form.school_level_lao = l ? l.name : '';
    },
    onTypeChange() {
      const t = this.types.find(t => t.id === this.form.schooltype_code);
      this.form.schooltype = t ? t.name : '';
    },
    levelClass(id) {
      return { 1: 'kinder', 2: 'primary', 3: 'middle', 4: 'high' }[id] || '';
    },
    openAdd() {
      this.form = EMPTY_FORM();
      this.formDistricts = [];
      this.modal = { show: true, isEdit: false };
      document.body.style.overflow = 'hidden';
    },
    async openEdit(school) {
      this.form = { ...EMPTY_FORM(), ...school };
      this.formDistricts = [];
      if (this.form.prov_id) await this.fetchDistricts(this.form.prov_id, 'formDistricts');
      this.modal = { show: true, isEdit: true };
      document.body.style.overflow = 'hidden';
    },
    closeModal() {
      this.modal.show = false;
      document.body.style.overflow = '';
    },
    async handleSubmit() {
      if (!this.form.school_id || !this.form.school_name_lao || !this.form.prov_id || !this.form.district_id) {
        this.showToast('error', 'ກະລຸນາປ້ອນຂໍ້ມູນທີ່ຈຳເປັນໃຫ້ຄົບ (*)');
        return;
      }
      this.submitting = true;
      try {
        let res;
        if (this.modal.isEdit) {
          const { _id, ...items } = this.form;
          res = await api.put(`/schools/${_id}`, { items });
        } else {
          res = await api.post('/schools', { items: this.form });
        }
        if (res.data.code === 1) {
          this.showToast('success', this.modal.isEdit ? 'ອັບເດດໂຮງຮຽນສຳເລັດ' : 'ເພີ່ມໂຮງຮຽນສຳເລັດ');
          this.closeModal();
          this.fetchSchools();
        } else {
          this.showToast('error', res.data.desc || 'ເກີດຂໍ້ຜິດພາດ');
        }
      } catch (e) {
        this.showToast('error', 'ບໍ່ສາມາດເຊື່ອມຕໍ່ກັບເຊີເວີ');
      } finally {
        this.submitting = false;
      }
    },
    confirmDel(school) {
      this.delModal = { show: true, item: school, loading: false };
      document.body.style.overflow = 'hidden';
    },
    async doDelete() {
      this.delModal.loading = true;
      try {
        const res = await api.delete(`/del/schools/${this.delModal.item._id}`);
        if (res.data.code === 1) {
          this.showToast('success', 'ລົບໂຮງຮຽນສຳເລັດ');
          this.delModal.show = false;
          document.body.style.overflow = '';
          if (this.schools.length === 1 && this.currentPage > 1) this.currentPage--;
          this.fetchSchools();
        } else {
          this.showToast('error', res.data.desc || 'ລົບບໍ່ສຳເລັດ');
        }
      } catch (e) {
        this.showToast('error', 'ເກີດຂໍ້ຜິດພາດໃນການລົບ');
      } finally {
        this.delModal.loading = false;
      }
    },
    // ──── Import Excel ────
    openImport() {
      this.imp.show = true;
      this.imp.step = 1;
      this.imp.rows = [];
      this.imp.importing = false;
      this.imp.importedCount = 0;
      document.body.style.overflow = 'hidden';
    },
    closeImport() {
      this.imp.show = false;
      document.body.style.overflow = '';
      if (this.imp.importedCount > 0) this.fetchSchools();
    },
    onDrop(e) {
      const file = e.dataTransfer.files[0];
      if (file) this.parseFile(file);
    },
    onFileChange(e) {
      const file = e.target.files[0];
      if (file) this.parseFile(file);
      e.target.value = '';
    },
    async parseFile(file) {
      this.imp.fileLoading = true;
      try {
        // 1. Load existing school_ids
        const res = await api.get('/getItems/schools/school_name_lao/school_id');
        const list = Array.isArray(res.data) ? res.data : (res.data.rows || []);
        this.imp.existingIds = new Set(list.map(x => String(x.value || '').trim()));

        // 2. Parse Excel
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });

        // 3. Map & validate each row
        const FIELDS = ['school_id','school_name_lao','school_name_eng','district_id','prov_id',
                        'commune_name','school_village','level_id','schooltype_code','school_lunch'];
        this.imp.rows = raw.map(row => {
          const r = {};
          FIELDS.forEach(f => { r[f] = String(row[f] !== undefined ? row[f] : '').trim(); });
          r.level_id = parseInt(r.level_id) || null;
          r.schooltype_code = parseInt(r.schooltype_code) || null;
          r.school_lunch = parseInt(r.school_lunch) !== undefined ? parseInt(r.school_lunch) : 1;

          if (!r.school_id || !r.school_name_lao || !r.district_id) {
            r._st = 'err';
          } else if (this.imp.existingIds.has(r.school_id)) {
            r._st = 'dup';
          } else {
            r._st = 'new';
          }
          return r;
        });

        this.imp.step = 2;
      } catch (e) {
        console.error(e);
        this.showToast('error', 'ອ່ານໄຟລ໌ບໍ່ສຳເລັດ: ' + e.message);
      } finally {
        this.imp.fileLoading = false;
      }
    },
    async doImport() {
      const toInsert = this.imp.rows
        .filter(r => r._st === 'new')
        .map(r => { const item = { ...r }; delete item._st; return item; });

      if (!toInsert.length) return;
      this.imp.importing = true;
      try {
        const res = await api.post('/insertMany', { table: 'schools', rows: toInsert });
        if (res.data.code === 1) {
          this.imp.importedCount = res.data.insertedCount || toInsert.length;
          this.imp.step = 3;
        } else {
          this.showToast('error', res.data.desc || 'ນຳເຂົ້າບໍ່ສຳເລັດ');
        }
      } catch (e) {
        this.showToast('error', 'ເກີດຂໍ້ຜິດພາດ: ' + e.message);
      } finally {
        this.imp.importing = false;
      }
    },
    downloadTemplate() {
      const ws = XLSX.utils.aoa_to_sheet([
        ['school_id','school_name_lao','school_name_eng','district_id','prov_id',
         'commune_name','school_village','level_id','schooltype_code','school_lunch'],
        ['0101001','ໂຮງຮຽນ ປ.ໂຮງ ກາງ','Houakhoang Primary','0101','01','ໂຮງ','ກາງ',2,1,1],
      ]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'schools');
      XLSX.writeFile(wb, 'school_import_template.xlsx');
    },
    statusLabel(s) {
      return s === 'new' ? 'ໃໝ່' : s === 'dup' ? 'ມີຢູ່ແລ້ວ' : 'ຂໍ້ມູນບໍ່ຄົບ';
    },
    statusIcon(s) {
      return s === 'new' ? 'fas fa-plus-circle' : s === 'dup' ? 'fas fa-exclamation-triangle' : 'fas fa-times-circle';
    },
    levelName(id) {
      return { 1: 'ອນຸບານ', 2: 'ປະຖົມ', 3: 'ມ.ຕົ້ນ', 4: 'ມ.ປາຍ' }[id] || '—';
    },
    // ──── End Import ────

    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = 'asc';
      }
      this.currentPage = 1;
      this.fetchSchools();
    },
    sortIcon(key) {
      if (this.sortKey !== key) return '⇅';
      return this.sortDir === 'asc' ? '↑' : '↓';
    },

    showToast(type, msg) {
      this.toast = { show: true, type, msg };
      setTimeout(() => { this.toast.show = false; }, 3500);
    },
  },
};
</script>

<style scoped>
/* ─────── Import Button ─────── */
.header-actions { display: flex; gap: 10px; flex-wrap: wrap; }
.btn-import {
  display: flex; align-items: center; gap: 8px;
  background: #fff; color: #1B5E20;
  border: 2px solid #2E7D32;
  padding: 10px 18px; border-radius: 10px; font-size: 14px; font-weight: 600;
  cursor: pointer; white-space: nowrap; transition: all .2s;
}
.btn-import:hover { background: #E8F5E9; box-shadow: 0 4px 12px rgba(27,94,32,.2); }

.btn-export {
  display: flex; align-items: center; gap: 8px;
  background: #fff; color: #1565C0;
  border: 2px solid #1565C0;
  padding: 10px 18px; border-radius: 10px; font-size: 14px; font-weight: 600;
  cursor: pointer; white-space: nowrap; transition: all .2s;
}
.btn-export:hover:not(:disabled) { background: #E3F2FD; box-shadow: 0 4px 12px rgba(21,101,192,.2); }
.btn-export:disabled { opacity: .6; cursor: not-allowed; }

/* ─────── Import Modal ─────── */
.modal-card.modal-xl { max-width: 860px; }
.modal-header.import-header { background: linear-gradient(135deg, #1B5E20, #2E7D32); }

.import-info-box {
  background: #E3F2FD; border-left: 4px solid #1976D2;
  padding: 12px 16px; border-radius: 8px; font-size: 13px; color: #0D47A1;
  margin-bottom: 20px; display: flex; gap: 10px; align-items: flex-start; flex-wrap: wrap;
}
.import-info-box code {
  background: rgba(13,71,161,.1); padding: 1px 6px; border-radius: 4px;
  font-family: monospace; font-size: 12px;
}
.file-drop-zone {
  border: 2px dashed #B0BEC5; border-radius: 12px; padding: 40px 24px;
  text-align: center; cursor: pointer; transition: all .2s; margin-bottom: 16px;
}
.file-drop-zone:hover { border-color: #2E7D32; background: #F1F8E9; }
.file-drop-icon { font-size: 2.5rem; color: #90A4AE; margin-bottom: 10px; display: block; }
.file-drop-zone p { margin: 6px 0; color: var(--text2); font-size: 14px; }
.file-drop-link { color: #2E7D32; font-weight: 600; text-decoration: underline; }
.file-drop-hint { font-size: 12px; color: var(--text3) !important; }
.import-loading { text-align: center; padding: 16px; color: var(--text3); font-size: 14px; }
.import-loading i { margin-right: 8px; color: #2E7D32; }

.btn-template {
  display: flex; align-items: center; gap: 6px;
  padding: 10px 18px; border: 1px solid var(--border); background: #f8f9fa;
  border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600; color: var(--text2);
}
.btn-template:hover { background: #e2e8f0; }

/* Summary badges */
.import-summary { display: flex; gap: 8px; margin-bottom: 14px; flex-wrap: wrap; align-items: center; }
.imp-badge {
  padding: 5px 12px; border-radius: 20px; font-size: 12px; font-weight: 700;
  display: flex; align-items: center; gap: 5px;
}
.imp-badge.total  { background: #EDE7F6; color: #4527A0; }
.imp-badge.new    { background: #E8F5E9; color: #1B5E20; }
.imp-badge.dup    { background: #FFF8E1; color: #E65100; }
.imp-badge.err    { background: #FFEBEE; color: #B71C1C; }

/* Preview table */
.import-table-wrap { max-height: 360px; overflow-y: auto; border-radius: 8px; border: 1px solid #f1f5f9; margin-bottom: 4px; }
.import-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.import-table th { background: #1B5E20; color: #fff; padding: 9px 12px; text-align: left; white-space: nowrap; }
.import-table td { padding: 9px 12px; border-bottom: 1px solid #f1f5f9; }
.import-table code { background: #EDE7F6; color: #4527A0; padding: 1px 5px; border-radius: 4px; font-size: 11px; }
.imp-row-new { background: #F9FBE7; }
.imp-row-dup { background: #FFFDE7; }
.imp-row-err { background: #FFF3F3; }

.imp-status { display: inline-flex; align-items: center; gap: 4px; padding: 3px 8px; border-radius: 10px; font-size: 11px; font-weight: 700; white-space: nowrap; }
.imp-status.new { background: #E8F5E9; color: #1B5E20; }
.imp-status.dup { background: #FFF8E1; color: #E65100; }
.imp-status.err { background: #FFEBEE; color: #B71C1C; }

.no-new-msg { font-size: 13px; color: var(--text3); display: flex; align-items: center; }

/* Done step */
.import-done { text-align: center; padding: 40px 24px; }
.import-done-icon { font-size: 3.5rem; color: #2E7D32; margin-bottom: 12px; display: block; }
.import-done h3 { color: #1B5E20; font-size: 1.3rem; margin: 0 0 8px; }
.import-done p { color: var(--text2); margin: 0 0 24px; }

/* ─────── Base ─────── */
.school-manage-container { animation: fadeIn 0.35s ease; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

/* ─────── Header ─────── */
.header-row  { display: flex; justify-content: space-between; align-items: flex-start; margin-top: 8px; flex-wrap: wrap; gap: 12px; }
.header-left h1 { font-size: 1.5rem; color: #311B92; margin: 0 0 4px; }
.header-left p  { color: var(--text3); font-size: 13px; margin: 0; }

.btn-add {
  display: flex; align-items: center; gap: 8px;
  background: linear-gradient(135deg, #4527A0, #311B92);
  color: #fff; border: none; padding: 10px 20px;
  border-radius: 10px; font-size: 14px; font-weight: 600;
  cursor: pointer; white-space: nowrap;
  box-shadow: 0 4px 14px rgba(69,39,160,.35);
  transition: all .2s;
}
.btn-add:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(69,39,160,.45); }

/* ─────── Stats ─────── */
.stats-strip { display: flex; gap: 16px; margin: 20px 0; flex-wrap: wrap; }
.stat-card   { display: flex; align-items: center; gap: 14px; background: #fff; padding: 16px 22px;
               border-radius: 12px; box-shadow: 0 2px 10px rgba(0,0,0,.06); flex: 1; min-width: 160px; }
.stat-icon   { width: 44px; height: 44px; border-radius: 10px; display: flex; align-items: center;
               justify-content: center; font-size: 18px; color: #fff; flex-shrink: 0; }
.stat-icon.blue   { background: linear-gradient(135deg, #4527A0, #5E35B1); }
.stat-icon.green  { background: linear-gradient(135deg, #1B5E20, #2E7D32); }
.stat-icon.purple { background: linear-gradient(135deg, #880E4F, #AD1457); }
.stat-num  { font-size: 1.6rem; font-weight: 700; color: #1e293b; line-height: 1; }
.stat-lbl  { font-size: 12px; color: var(--text3); margin-top: 3px; }

/* ─────── Filter ─────── */
.filter-panel { background: #fff; border-radius: 12px; padding: 14px 18px;
                box-shadow: 0 2px 10px rgba(0,0,0,.06); margin-bottom: 16px; }
.filter-row   { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.search-wrap  { position: relative; flex: 1; min-width: 220px; }
.search-wrap input {
  width: 100%; padding: 10px 36px 10px 36px; border: 1px solid var(--border);
  border-radius: 8px; font-size: 13px; transition: border .2s;
}
.search-wrap input:focus { border-color: #4527A0; outline: none; }
.search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--text3); font-size: 13px; }
.clear-btn   { position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
               background: none; border: none; color: var(--text3); cursor: pointer; padding: 0; }
.filter-select {
  padding: 10px 12px; border: 1px solid var(--border); border-radius: 8px;
  font-size: 13px; min-width: 140px; cursor: pointer;
}
.filter-select:focus { border-color: #4527A0; outline: none; }
.btn-search {
  display: flex; align-items: center; gap: 6px;
  background: #4527A0; color: #fff; border: none;
  padding: 10px 18px; border-radius: 8px; font-size: 13px;
  font-weight: 600; cursor: pointer; white-space: nowrap; transition: background .2s;
}
.btn-search:hover { background: #311B92; }
.btn-reset {
  background: #f1f5f9; border: 1px solid var(--border); color: var(--text2);
  padding: 10px 14px; border-radius: 8px; cursor: pointer; font-size: 13px;
}
.btn-reset:hover { background: #e2e8f0; }

/* ─────── Table ─────── */
.panel-body { padding: 0; }
.table-wrap { overflow-x: auto; border-radius: 0 0 12px 12px; }
table { width: 100%; border-collapse: collapse; min-width: 860px; }
th { background: linear-gradient(135deg, #4527A0, #5E35B1); color: #fff; padding: 12px 14px;
     text-align: left; font-size: 13px; font-weight: 600; white-space: nowrap; }
.th-sort { cursor: pointer; user-select: none; transition: background .15s; }
.th-sort:hover { background: linear-gradient(135deg, #311B92, #4527A0); }
.sort-icon { font-size: 12px; margin-left: 4px; opacity: .75; font-style: normal; }
td { padding: 12px 14px; font-size: 13px; color: var(--text1); border-bottom: 1px solid #f1f5f9; white-space: nowrap; }
.row-even { background: #fff; }
.row-odd  { background: #fafbff; }
tr:hover td { background: #f0ebff !important; }
.td-center  { text-align: center; }
.td-loading, .td-empty { color: var(--text3); padding: 48px 0 !important; text-align: center; }
.td-empty i  { display: block; margin-bottom: 10px; color: #c5b8ff; }
.td-empty div { white-space: nowrap; font-size: 14px; }
td.td-center { text-align: center; }
.td-num  { color: var(--text3); font-size: 12px; }
.td-name .school-name { font-weight: 600; color: #311B92; }
.td-name .school-sub  { font-size: 11px; color: var(--text3); margin-top: 2px; }
.td-loc  { color: var(--text2); }

.emis-badge { background: #EDE7F6; color: #4527A0; padding: 3px 8px;
              border-radius: 6px; font-size: 11px; font-weight: 700; font-family: monospace; }

.level-badge { padding: 3px 9px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.level-badge.kinder  { background: #FFF3E0; color: #E65100; }
.level-badge.primary { background: #E8F5E9; color: #1B5E20; }
.level-badge.middle  { background: #E3F2FD; color: #0D47A1; }
.level-badge.high    { background: #F3E5F5; color: #6A1B9A; }

.type-badge { padding: 3px 9px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.type-badge.state   { background: #E8EAF6; color: #283593; }
.type-badge.private { background: #FCE4EC; color: #880E4F; }

.lunch-pill { padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; }
.lunch-pill.yes { background: #E8F5E9; color: #1B5E20; }
.lunch-pill.no  { background: #F5F5F5; color: #9E9E9E; }

.action-btn { width: 30px; height: 30px; border: none; border-radius: 7px;
              cursor: pointer; font-size: 13px; transition: all .18s; margin: 0 2px; }
.action-btn.edit   { background: #EDE7F6; color: #4527A0; }
.action-btn.delete { background: #FFEBEE; color: #C62828; }
.action-btn.edit:hover   { background: #4527A0; color: #fff; }
.action-btn.delete:hover { background: #C62828; color: #fff; }

/* ─────── Pagination ─────── */
.pagination-bar {
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 20px; border-top: 1px solid #f1f5f9; flex-wrap: wrap; gap: 10px;
}
.page-info { font-size: 13px; color: var(--text3); }
.page-controls { display: flex; gap: 4px; flex-wrap: wrap; }
.page-btn {
  min-width: 32px; height: 32px; padding: 0 8px; border: 1px solid var(--border);
  background: #fff; border-radius: 6px; cursor: pointer; font-size: 13px; transition: all .18s;
}
.page-btn:hover:not(:disabled) { background: #EDE7F6; border-color: #4527A0; color: #4527A0; }
.page-btn.active  { background: #4527A0; color: #fff; border-color: #4527A0; font-weight: 700; }
.page-btn:disabled { opacity: .45; cursor: not-allowed; }
.page-btn.ellipsis { cursor: default; background: none; border: none; }
.page-size-select { padding: 6px 10px; border: 1px solid var(--border); border-radius: 6px; font-size: 12px; }

/* ─────── Modal Overlay ─────── */
.modal-overlay {
  position: fixed; inset: 0; background: rgba(17,24,39,.5);
  backdrop-filter: blur(4px); display: flex; align-items: center;
  justify-content: center; z-index: 1000; padding: 16px;
}
.modal-card {
  background: #fff; border-radius: 16px; width: 100%;
  max-width: 680px; box-shadow: 0 25px 50px rgba(0,0,0,.18);
  display: flex; flex-direction: column; max-height: 90vh;
}
.modal-card.modal-sm { max-width: 420px; }

.modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 20px 24px; border-bottom: 1px solid #f1f5f9;
  border-radius: 16px 16px 0 0;
  background: linear-gradient(135deg, #4527A0, #311B92);
}
.modal-header.danger { background: linear-gradient(135deg, #B71C1C, #C62828); }
.modal-title { display: flex; align-items: center; gap: 12px; color: #fff; font-size: 16px; font-weight: 700; }
.modal-icon-wrap {
  width: 34px; height: 34px; background: rgba(255,255,255,.2);
  border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 15px;
}
.modal-close { background: rgba(255,255,255,.15); border: none; border-radius: 8px;
               color: #fff; width: 30px; height: 30px; cursor: pointer; font-size: 14px; transition: background .18s; }
.modal-close:hover { background: rgba(255,255,255,.3); }

.modal-body { padding: 24px; overflow-y: auto; }

.form-row { display: flex; gap: 16px; margin-bottom: 16px; }
.form-group { flex: 1; display: flex; flex-direction: column; gap: 6px; }
.form-group.flex2 { flex: 2; }
.form-group label { font-size: 13px; font-weight: 600; color: var(--text2); }
.form-group input, .form-group select {
  padding: 10px 12px; border: 1px solid var(--border);
  border-radius: 8px; font-size: 13px; transition: border .2s;
}
.form-group input:focus, .form-group select:focus { border-color: #4527A0; outline: none; box-shadow: 0 0 0 3px rgba(69,39,160,.12); }
.form-group input:disabled, .form-group select:disabled { background: #f8f9fa; color: var(--text3); }
.req { color: #e53e3e; }

.radio-group { display: flex; gap: 12px; }
.radio-opt {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 16px; border: 1.5px solid var(--border);
  border-radius: 9px; cursor: pointer; font-size: 13px; font-weight: 500;
  transition: all .18s; flex: 1; justify-content: center;
}
.radio-opt input[type="radio"] { display: none; }
.radio-opt.selected { border-color: #4527A0; background: #EDE7F6; color: #4527A0; }
.radio-opt:hover:not(.selected) { border-color: #9575CD; }

.modal-footer {
  padding: 16px 24px; border-top: 1px solid #f1f5f9;
  display: flex; justify-content: flex-end; gap: 10px;
  border-radius: 0 0 16px 16px;
}
.btn-cancel {
  padding: 10px 20px; border: 1px solid var(--border); background: #fff;
  border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600; color: var(--text2);
}
.btn-cancel:hover { background: #f8f9fa; }
.btn-save {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 24px; background: linear-gradient(135deg, #4527A0, #311B92);
  color: #fff; border: none; border-radius: 8px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all .2s;
}
.btn-save:hover:not(:disabled) { box-shadow: 0 4px 12px rgba(69,39,160,.4); }
.btn-save:disabled { opacity: .65; cursor: not-allowed; }
.btn-danger {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 24px; background: linear-gradient(135deg, #B71C1C, #C62828);
  color: #fff; border: none; border-radius: 8px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all .2s;
}
.btn-danger:hover:not(:disabled) { box-shadow: 0 4px 12px rgba(183,28,28,.4); }
.btn-danger:disabled { opacity: .65; cursor: not-allowed; }

/* ─── Delete modal body ─── */
.del-body { text-align: center; }
.del-body p { color: var(--text2); font-size: 14px; margin: 8px 0; }
.del-target {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  background: #FFEBEE; border-radius: 10px; padding: 14px 20px; margin: 12px 0;
  color: #B71C1C; font-size: 15px;
}
.del-warn { font-size: 12px; color: var(--text3); }
code { background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-family: monospace; }

/* ─── Toast ─── */
.toast {
  position: fixed; bottom: 28px; right: 28px; z-index: 2000;
  display: flex; align-items: center; gap: 10px;
  padding: 14px 20px; border-radius: 12px; font-size: 14px; font-weight: 600;
  color: #fff; box-shadow: 0 8px 24px rgba(0,0,0,.18); min-width: 240px;
}
.toast.success { background: linear-gradient(135deg, #1B5E20, #2E7D32); }
.toast.error   { background: linear-gradient(135deg, #B71C1C, #C62828); }

/* ─── Transitions ─── */
.modal-fade-enter-active, .modal-fade-leave-active { transition: all .25s ease; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter .modal-card, .modal-fade-leave-to .modal-card { transform: translateY(-16px) scale(.97); }

.toast-slide-enter-active, .toast-slide-leave-active { transition: all .3s ease; }
.toast-slide-enter { opacity: 0; transform: translateX(40px); }
.toast-slide-leave-to { opacity: 0; transform: translateX(40px); }

/* ─── Responsive ─── */
@media (max-width: 640px) {
  .form-row   { flex-direction: column; }
  .header-row { flex-direction: column; }
  .stats-strip { gap: 10px; }
  .stat-card   { min-width: unset; }
}
</style>
